'use client';

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import { getImageProps } from 'next/image';
import { FC, useEffect, useRef } from 'react';
import { preload } from 'react-dom';
import Image from '@/components/Image';
import LeadMagnetProgress from '@/components/LeadMagnet/Progress';
import progressCss from '@/components/LeadMagnet/Progress/style.module.scss';
import ArrowPrev from '@/components/shared/Icon/ArrowPrev';
import Check from '@/components/shared/Icon/Check';
import Cross from '@/components/shared/Icon/Cross';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import useDevice from '@/hooks/useDevice';
import { gsap } from '@/lib/gsap';
import { buildEntrance, prefersReducedMotion, settleEntrance } from '@/lib/lead-magnet/animation';
import { formatProgressLabel } from '@/lib/lead-magnet/format';
import { resolveAnswerIcon } from '@/lib/lead-magnet/icons';
import { resolveResponsiveMedia } from '@/lib/lead-magnet/scoring';
import { LeadMagnetMedia, QuizQuestion } from '@/types/lead-magnet';
import css from './style.module.scss';
const VISUAL_SIZES = '(max-width: 991px) 100vw, 33vw';
const VISUAL_FALLBACK = {
  width: 525,
  height: 468
};
const VISUAL_QUALITY = 75;
interface Props {
  question: QuizQuestion;
  /** Only its visual is used, to warm the cache before the screen is needed. */
  nextQuestion: QuizQuestion | null;
  index: number;
  total: number;
  score: number;
  progressLabelFormat: string;
  backLabel: string;
  selectedValue: string | null;
  canGoBack: boolean;
  background: LeadMagnetMedia | null;
  onAnswer: (value: string) => void;
  onBack: () => void;
}
const LeadMagnetQuestionScreen: FC<Props> = t0 => {
  const $ = _c(62);
  const {
    question,
    nextQuestion,
    index,
    total,
    score,
    progressLabelFormat,
    backLabel,
    selectedValue,
    canGoBack,
    background,
    onAnswer,
    onBack
  } = t0;
  const device = useDevice();
  const t1 = device === "mobile";
  let t2;
  if ($[0] !== question.visual || $[1] !== t1) {
    t2 = resolveResponsiveMedia(question.visual, t1);
    $[0] = question.visual;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const visual = t2;
  const step = index + 1;
  let t3;
  if ($[3] !== progressLabelFormat || $[4] !== score || $[5] !== step || $[6] !== total) {
    t3 = formatProgressLabel(progressLabelFormat, {
      current: step,
      score,
      total
    });
    $[3] = progressLabelFormat;
    $[4] = score;
    $[5] = step;
    $[6] = total;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const progressLabel = t3;
  const t4 = nextQuestion?.visual ?? null;
  const t5 = device === "mobile";
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = resolveResponsiveMedia(t4, t5);
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const nextVisual = t6;
  let t7;
  let t8;
  if ($[11] !== nextVisual) {
    t7 = () => {
      if (!nextVisual) {
        return;
      }
      const {
        props
      } = getImageProps({
        src: nextVisual.url,
        alt: "",
        width: nextVisual.width ?? VISUAL_FALLBACK.width,
        height: nextVisual.height ?? VISUAL_FALLBACK.height,
        sizes: VISUAL_SIZES,
        quality: VISUAL_QUALITY
      });
      preload(props.src, {
        as: "image",
        imageSrcSet: props.srcSet,
        imageSizes: props.sizes
      });
    };
    t8 = [nextVisual];
    $[11] = nextVisual;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  const rootRef = useRef(null);
  const hasImageAnimatedRef = useRef(false);
  let t10;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const root = rootRef.current;
      if (!root) {
        return;
      }
      const filled = root.querySelectorAll(`.${progressCss.segmentFilled}`);
      const latest = filled[filled.length - 1];
      if (latest && !prefersReducedMotion()) {
        gsap.fromTo(latest, {
          scaleX: 0
        }, {
          scaleX: 1,
          duration: 0.45,
          ease: "power2.out",
          transformOrigin: "left center"
        });
      }
      buildEntrance([{
        targets: root.querySelector(`.${css.category}`),
        position: 0,
        duration: 0.5
      }, {
        targets: root.querySelector(`.${css.title}`),
        role: "title",
        position: 0.06,
        duration: 0.65,
        from: {
          y: 18
        }
      }, {
        targets: root.querySelectorAll(`.${css.answers} > li`),
        role: "control",
        position: 0.28,
        duration: 0.5,
        stagger: 0.07
      }]);
      const image = root.querySelector(`.${css.visualImage}`);
      if (!image) {
        return;
      }
      let cancelled = false;
      let imageTimeline = null;
      const claim = () => {
        if (cancelled || hasImageAnimatedRef.current) {
          return false;
        }
        hasImageAnimatedRef.current = true;
        return true;
      };
      const animateImage = () => {
        if (!claim()) {
          return;
        }
        imageTimeline = buildEntrance([{
          targets: image,
          role: "image",
          position: 0,
          duration: 0.8,
          clearProps: false,
          vars: {
            onComplete: () => image.style.removeProperty("will-change")
          }
        }]);
      };
      const settleImage = () => {
        if (!claim()) {
          return;
        }
        settleEntrance(image, false);
        image.style.removeProperty("will-change");
      };
      if (prefersReducedMotion()) {
        settleImage();
        return;
      }
      const decodeThenAnimate = () => image.decode().then(animateImage, settleImage);
      if (image.complete && image.naturalWidth > 0) {
        decodeThenAnimate();
      } else {
        image.addEventListener("load", decodeThenAnimate, {
          once: true
        });
        image.addEventListener("error", settleImage, {
          once: true
        });
      }
      return () => {
        cancelled = true;
        imageTimeline?.kill();
        image.removeEventListener("load", decodeThenAnimate);
        image.removeEventListener("error", settleImage);
      };
    };
    t10 = {
      scope: rootRef
    };
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  useGSAP(t9, t10);
  let t11;
  if ($[16] !== background) {
    t11 = background ? {
      "--questions-bg": `url(${background.url})`
    } as React.CSSProperties : undefined;
    $[16] = background;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== question.category) {
    t12 = question.category ? <Typography uppercase={true} variant="body-m" tag="p" className={css.category}>{question.category}</Typography> : null;
    $[18] = question.category;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== question.question) {
    t13 = <h2 className={css.title}><StrapiRichContent isInlineContent={true} content={question.question} /></h2>;
    $[20] = question.question;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== onAnswer || $[23] !== question.answers || $[24] !== selectedValue) {
    let t15;
    if ($[26] !== onAnswer || $[27] !== selectedValue) {
      t15 = answer => {
        const icon = resolveAnswerIcon(answer);
        return <li key={answer.value}><button type="button" aria-pressed={selectedValue === answer.value} className={clsx(css.answer, selectedValue === answer.value && css.answerSelected)} onClick={() => onAnswer(answer.value)}>{icon === "check" ? <Check className={css.answerIcon} /> : null}{icon === "cross" ? <Cross className={css.answerIcon} /> : null}<Typography uppercase={true} responsive={false} variant="body-m" tag="span" className={css.answerLabel}>{answer.label}</Typography></button></li>;
      };
      $[26] = onAnswer;
      $[27] = selectedValue;
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    t14 = question.answers.map(t15);
    $[22] = onAnswer;
    $[23] = question.answers;
    $[24] = selectedValue;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[29] !== t14) {
    t15 = <ul className={css.answers}>{t14}</ul>;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t12 || $[32] !== t13 || $[33] !== t15) {
    t16 = <div className={css.main}>{t12}{t13}{t15}</div>;
    $[31] = t12;
    $[32] = t13;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== backLabel || $[36] !== canGoBack || $[37] !== onBack) {
    t17 = canGoBack ? <button type="button" className={css.back} aria-label={backLabel} onClick={onBack}><ArrowPrev size={14} className={css.backIcon} /><Typography uppercase={true} variant="body-s" tag="span">{backLabel}</Typography></button> : <span />;
    $[35] = backLabel;
    $[36] = canGoBack;
    $[37] = onBack;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== progressLabel) {
    t18 = <Typography uppercase={true} variant="body-s" tag="span" className={css.counter}>{progressLabel}</Typography>;
    $[39] = progressLabel;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t17 || $[42] !== t18) {
    t19 = <div className={css.footerMeta}>{t17}{t18}</div>;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== progressLabel || $[45] !== step || $[46] !== total) {
    t20 = <LeadMagnetProgress filled={step} total={total} label={progressLabel} />;
    $[44] = progressLabel;
    $[45] = step;
    $[46] = total;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t19 || $[49] !== t20) {
    t21 = <footer className={css.footer}>{t19}{t20}</footer>;
    $[48] = t19;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t16 || $[52] !== t21) {
    t22 = <div className={css.content}>{t16}{t21}</div>;
    $[51] = t16;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== visual) {
    t23 = visual ? <Image preload={true} className={css.visualImage} src={visual.url} alt={visual.alt} width={visual.width ?? VISUAL_FALLBACK.width} height={visual.height ?? VISUAL_FALLBACK.height} quality={VISUAL_QUALITY} sizes={VISUAL_SIZES} /> : null;
    $[54] = visual;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t23) {
    t24 = <div className={css.visual}>{t23}</div>;
    $[56] = t23;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t11 || $[59] !== t22 || $[60] !== t24) {
    t25 = <section ref={rootRef} className={css.question} style={t11}>{t22}{t24}</section>;
    $[58] = t11;
    $[59] = t22;
    $[60] = t24;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  return t25;
};
export default LeadMagnetQuestionScreen;